// Pick from everything a vendor supplies, with quantities, to start a PO or add to one. Each row
// shows the vendor's price at the chosen qty, MOQ and multiple, stock available at HHL and what's already on order.
// The usual table: tick rows (a click on a row, its box, Space, or shift-click for a range), then type each one's Qty
// (click it; Enter saves and moves down).
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { Item, Po, PoLine, Vendor, VendorItem } from "../../../../shared/db.gen.ts";
import { isOpen, priceFor, qtyWarnings, validQty } from "../../../../shared/po.ts";
import { type HhlStock, hhlStock, stockNote } from "../../../../shared/stock.ts";
import { money, qty } from "../../lib/format.ts";
import { useRows } from "../../lib/sync.ts";
import { Button, cx, Input, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { DialogLayer } from "../../ui/DialogLayer.tsx";
import { toast } from "../../ui/toast.tsx";
import { addLines, createPo, parseNumber, vendorLabel } from "./lib.ts";

type Row = { id: string; link: VendorItem; item: Item; atHhl: HhlStock | undefined; onOrder: number; already: boolean };

export function VendorItemsDialog({ vendor, po, lines = [], onClose }: { vendor: Vendor; po?: Po; lines?: PoLine[]; onClose: () => void }) {
  const links = useRows("vendor_item");
  const items = useRows("item");
  const stock = useRows("stock_level");
  const ledger = useRows("stock_balance"); // materials and sub-assemblies
  const locations = useRows("location"); // Consignment --- HHL's stock, not a CM's
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Map<string, string>>(() => new Map()); // item id → qty as typed
  const [busy, setBusy] = useState(false);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- the table's View and Columns

  const rows = useMemo<Row[]>(() => {
    const itemById = new Map(items.map((i) => [i.id, i]));
    const atHhl = hhlStock(stock, ledger, locations);
    const openPos = new Set(pos.filter((p) => isOpen(p.status) && p.status !== "draft").map((p) => p.id));
    const onOrder = new Map<string, number>();
    for (const l of poLines)
      if (openPos.has(l.po_id)) onOrder.set(l.item_id, (onOrder.get(l.item_id) ?? 0) + Math.max(0, Number(l.qty) - Number(l.qty_received)));
    const already = new Set(lines.map((l) => l.item_id));
    return links
      .filter((l) => l.vendor_id === vendor.id)
      .flatMap((link) => {
        const item = itemById.get(link.item_id);
        return item && !item.archived
          ? [{ id: item.id, link, item, atHhl: atHhl.get(item.id), onOrder: onOrder.get(item.id) ?? 0, already: already.has(item.id) }]
          : [];
      });
  }, [links, items, stock, ledger, locations, pos, poLines, lines, vendor.id]);

  const toggle = (itemId: string, defaultQty: number) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.set(itemId, String(defaultQty));
      return next;
    });
  // The table's ticks: a newly ticked item starts at its MOQ and order multiple, one ticked before keeps what was typed.
  const select = (ids: Set<string>) =>
    setPicked((prev) => {
      const next = new Map<string, string>();
      for (const r of rows) if (ids.has(r.id)) next.set(r.id, prev.get(r.id) ?? String(validQty(r.link)));
      return next;
    });
  /** The qty a row prices at: what's typed once it's ticked, else its MOQ and multiple. */
  const qtyOf = (r: Row) => {
    const typed = parseNumber(picked.get(r.id) ?? "");
    return picked.has(r.id) && typed ? typed : validQty(r.link);
  };
  const warningsOf = (r: Row) => {
    const typed = parseNumber(picked.get(r.id) ?? "");
    return picked.has(r.id) && typed ? qtyWarnings(r.link, typed) : [];
  };
  const columns: Column<Row>[] = [
    { key: "photo", header: "", width: "40px", value: () => "", cell: (r) => <Thumb src={r.item.photo_url} size={24} /> },
    { key: "sku", header: "SKU", width: "120px", value: (r) => r.item.sku, cell: (r) => <span className="truncate font-mono text-[12px]">{r.item.sku}</span> },
    {
      key: "name",
      header: "Item",
      width: "minmax(180px,1fr)",
      value: (r) => r.item.name,
      cell: (r) => (
        <>
          <span className="truncate">{r.item.name}</span>
          {r.already && <span className="shrink-0 text-[11px] text-faint">on this PO</span>}
        </>
      ),
    },
    {
      key: "vendor_sku",
      header: "Vendor SKU",
      width: "110px",
      value: (r) => r.link.vendor_sku,
      cell: (r) => <span className="truncate font-mono text-[12px] text-muted">{r.link.vendor_sku}</span>,
    },
    { key: "vendor_name", header: "Vendor name", width: "minmax(140px,1fr)", optional: true, value: (r) => r.link.vendor_name },
    {
      key: "price",
      header: "Price",
      width: "90px",
      align: "right",
      value: (r) => priceFor(r.link, qtyOf(r)) ?? "",
      cell: (r) => <span className="font-mono text-[12px]">{money(priceFor(r.link, qtyOf(r)), r.link.currency) || <span className="text-warn">—</span>}</span>,
    },
    {
      key: "moq",
      header: "MOQ",
      width: "90px",
      align: "right",
      value: (r) => Number(r.link.moq) || 0,
      text: (r) => `${r.link.moq ? qty(r.link.moq) : ""}${r.link.order_multiple > 1 ? ` ×${qty(r.link.order_multiple)}` : ""}`,
      cell: (r) => (
        <span className="font-mono text-[12px] text-muted">
          {r.link.moq ? qty(r.link.moq) : ""}
          {r.link.order_multiple > 1 ? ` ×${qty(r.link.order_multiple)}` : ""}
        </span>
      ),
    },
    {
      key: "available",
      header: "Available",
      headerTitle: "At HHL: on hand less units committed to customer orders (not shipped yet)",
      width: "84px",
      align: "right",
      value: (r) => r.atHhl?.available ?? 0,
      cell: (r) => (
        <span className={cx("font-mono text-[12px]", (r.atHhl?.available ?? 0) < 0 && "text-danger")} title={stockNote(r.atHhl) ?? undefined}>
          {qty(r.atHhl?.available ?? 0)}
        </span>
      ),
    },
    {
      key: "onOrder",
      header: "On order",
      width: "80px",
      align: "right",
      cell: (r) => <span className="font-mono text-[12px] text-muted">{r.onOrder ? qty(r.onOrder) : ""}</span>,
    },
    {
      key: "qty",
      header: "Qty",
      headerTitle: "Once it's ticked: click to type how many",
      width: "90px",
      align: "right",
      value: (r) => (picked.has(r.id) ? (parseNumber(picked.get(r.id) ?? "") ?? "") : ""),
      cell: (r) => {
        const warnings = warningsOf(r);
        return (
          picked.has(r.id) && (
            <span className={cx("font-mono text-[12px]", warnings.length && "text-warn")} title={warnings.map((w) => w.message).join(". ") || undefined}>
              {picked.get(r.id) || "—"}
            </span>
          )
        );
      },
      edit: {
        can: (r) => picked.has(r.id),
        get: (r) => picked.get(r.id) ?? "",
        save: (r, text) => setPicked((prev) => (prev.has(r.id) ? new Map(prev).set(r.id, text.trim()) : prev)),
      },
    },
  ];

  const submit = async () => {
    const chosen = rows.filter((r) => picked.has(r.item.id));
    const bad = chosen.find((r) => {
      const n = parseNumber(picked.get(r.item.id) ?? "");
      return n == null || n <= 0;
    });
    if (bad) return toast.error(`Enter a quantity for ${bad.item.sku || bad.item.name}`);
    const newLines = chosen.map((r) => ({ item: r.item, link: r.link, qty: parseNumber(picked.get(r.item.id) ?? "") ?? undefined }));
    setBusy(true);
    const ok = po ? await addLines(po, lines, newLines) : !!(await createPo(vendor, newLines));
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="flex max-h-[80vh] w-[1000px] max-w-[calc(100vw-32px)] flex-col rounded-xl border border-line bg-surface shadow-pop"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div className="flex items-center gap-3 border-b border-line p-4">
          <div className="text-[15px] font-semibold">
            {po ? `Add ${vendorLabel(vendor)}'s items to ${po.number}` : `New PO from ${vendorLabel(vendor)}'s items`}
          </div>
          <div ref={setSlot} className="ml-auto flex items-center gap-1" />
          <div className="relative w-64">
            <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-faint" />
            <Input autoFocus value={query} placeholder="Filter…" onChange={(e) => setQuery(e.target.value)} className="pl-8" />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          <DataTable
            fit={14}
            rows={rows}
            columns={columns}
            search={query}
            defaultSort={{ key: "sku" }}
            onOpen={(r) => toggle(r.id, validQty(r.link))}
            selection={{ selected: new Set(picked.keys()), onChange: select }}
            editing
            exportName="Vendor items"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["item", "items"]}
            empty={rows.length ? "No matches" : `${vendorLabel(vendor)} isn't linked to any items yet. Add vendors from each item's panel.`}
          />
        </div>
        <div className="flex items-center gap-2 border-t border-line p-3">
          <span className="text-muted">
            {picked.size ? `${picked.size} selected` : "Tick the items to order; quantities start at the MOQ and order multiple."}
          </span>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || (!picked.size && !!po)} onClick={submit}>
            {po
              ? `Add ${picked.size || ""} ${picked.size === 1 ? "line" : "lines"}`
              : picked.size
                ? `Create PO with ${picked.size} ${picked.size === 1 ? "line" : "lines"}`
                : "Create empty PO"}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
